import { useState } from 'react';
import { Box, Typography } from '@mui/material';

import { Filter } from '../../../http/meas-service';
import NodeFilter from './NodeFilter';
import LocationFilter from './LocationFilter';
import DeviceFilter from './DeviceFilter';
import SnsFilter from './SnsFilter';
import OverdueFilter, { OverdueFilterValue } from './OverdueFilter';

//=================================================================================================
function buildFilter(
    nodeId: number,
    locationIds: number[],
    deviceIds: number[],
    snsIds: number[],
    overdue: OverdueFilterValue,
): Omit<Filter, 'fr' | 'to'> {
    // prettier-ignore
    return {
        node: nodeId,
        lo:  locationIds.length > 0 ? locationIds : undefined,
        dev: deviceIds.length   > 0 ? deviceIds   : undefined,
        sns: snsIds.length      > 0 ? snsIds       : undefined,
        ov:  overdue === 'all' ? undefined : overdue === 'historical',
    };
}

//=================================================================================================
function Section({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1, pt: 1, display: 'block' }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}

//=================================================================================================
export interface FilterLayoutProps {
    initialNodeId: number;
    initialLocationIds?: number[];
    initialDeviceIds?: number[];
    initialSnsIds?: number[];
    onChange: (filter: Omit<Filter, 'fr' | 'to'>) => void;
}

export default function FilterLayout({
    initialNodeId,
    initialLocationIds = [],
    initialDeviceIds = [],
    initialSnsIds = [],
    onChange,
}: FilterLayoutProps) {
    const [nodeId, setNodeId] = useState(initialNodeId);
    const [locationIds, setLocationIds] = useState<number[]>(initialLocationIds);
    const [deviceIds, setDeviceIds] = useState<number[]>(initialDeviceIds);
    const [snsIds, setSnsIds] = useState<number[]>(initialSnsIds);
    const [overdue, setOverdue] = useState<OverdueFilterValue>('all');

    const emit = (nid: number, lids: number[], dids: number[], sids: number[], ov: OverdueFilterValue) =>
        onChange(buildFilter(nid, lids, dids, sids, ov));

    const handleNodeChange = (nid: number) => {
        setNodeId(nid);
        setLocationIds([]);
        setDeviceIds([]);
        setSnsIds([]);
        emit(nid, [], [], [], overdue);
    };

    const handleLocationChange = (lids: number[]) => {
        setLocationIds(lids);
        emit(nodeId, lids, deviceIds, snsIds, overdue);
    };

    const handleDeviceChange = (dids: number[]) => {
        setDeviceIds(dids);
        emit(nodeId, locationIds, dids, snsIds, overdue);
    };

    const handleSnsChange = (sids: number[]) => {
        setSnsIds(sids);
        emit(nodeId, locationIds, deviceIds, sids, overdue);
    };

    const handleOverdueChange = (ov: OverdueFilterValue) => {
        setOverdue(ov);
        emit(nodeId, locationIds, deviceIds, snsIds, ov);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Section label="Узел">
                <NodeFilter selectedNodeId={initialNodeId} onChange={handleNodeChange} />
            </Section>

            <Section label="Локации">
                <LocationFilter
                    nodeId={nodeId}
                    selectedLocationIds={initialLocationIds}
                    onChange={handleLocationChange}
                />
            </Section>

            <Section label="Устройства">
                <DeviceFilter
                    key={nodeId}
                    nodeId={nodeId}
                    selectedDeviceIds={initialDeviceIds}
                    onChange={handleDeviceChange}
                />
            </Section>

            <Section label="Датчики">
                <SnsFilter key={nodeId} nodeId={nodeId} selectedSnsIds={initialSnsIds} onChange={handleSnsChange} />
            </Section>

            <Section label="Исторические">
                <Box sx={{ px: 1, pb: 1 }}>
                    <OverdueFilter value={overdue} onChange={handleOverdueChange} />
                </Box>
            </Section>
        </Box>
    );
}
